import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { API_BASE_URL } from '../config';


// Async thunk for fetching messages
export const fetchMessages = createAsyncThunk(
  'quickMessage/fetchMessages',
  async (adminId, { rejectWithValue }) => {
    try {
      const response = await fetch(`${API_BASE_URL}/quick-messages?adminId=${adminId}`);
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to fetch messages');
      return data;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to fetch messages');
    }
  }
);

// Async thunk for creating a message
export const createMessage = createAsyncThunk(
  'quickMessage/createMessage',
  async ({ adminId, text }, { rejectWithValue }) => {
    try {
      const response = await fetch(`${API_BASE_URL}/quick-messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ adminId, text }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to create message');
      return data;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to create message');
    }
  }
);

// Async thunk for updating a message
export const updateMessage = createAsyncThunk(
  'quickMessage/updateMessage',
  async ({ id, text }, { rejectWithValue }) => {
    try {
      const response = await fetch(`${API_BASE_URL}/quick-messages/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to update message');
      return data;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to update message');
    }
  }
);

// Async thunk for deleting a message
export const deleteMessage = createAsyncThunk(
  'quickMessage/deleteMessage',
  async (id, { rejectWithValue }) => {
    try {
      const response = await fetch(`${API_BASE_URL}/quick-messages/${id}`, {
        method: 'DELETE',
      });
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to delete message');
      }
      return id; // return the deleted id so we can remove it from state
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to delete message');
    }
  }
);

const initialState = {
  messages: [],
  loading: false,
  error: null,
};

const quickMessageSlice = createSlice({
  name: 'quickMessage',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch messages
      .addCase(fetchMessages.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchMessages.fulfilled, (state, action) => {
        state.loading = false;
        state.messages = action.payload;
      })
      .addCase(fetchMessages.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Create message
      .addCase(createMessage.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createMessage.fulfilled, (state, action) => {
        state.loading = false;
        state.messages.unshift(action.payload); // Add to the top of the list
      })
      .addCase(createMessage.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Update message
      .addCase(updateMessage.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateMessage.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.messages.findIndex((msg) => msg._id === action.payload._id);
        if (index !== -1) {
          state.messages[index] = action.payload;
        }
      })
      .addCase(updateMessage.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Delete message
      .addCase(deleteMessage.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteMessage.fulfilled, (state, action) => {
        state.loading = false;
        state.messages = state.messages.filter((msg) => msg._id !== action.payload);
      })
      .addCase(deleteMessage.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  }
});

export const { clearError } = quickMessageSlice.actions;
export default quickMessageSlice.reducer;
